import { actionIconStyles, PURPLE, DISABLED } from "../styles/shared/actionIconStyles";
import React, { useContext } from "react";
import FigmaIcon from "./icons/FigmaIcon";
import { Box, Stack } from "@mui/material";
import { normalizeContactActionHref } from "../utils/contactChannels";
import { GenericDataTableContext } from "./GenericDataTableContext";

const CONTACT_TYPES = ["website", "email", "phone"];
const SOCIAL_TYPES = ["linkedin", "facebook", "twitter", "x", "google", "instagram", "youtube", "tiktok"];
const OTHER_TYPES = ["sms", "resume"];

function BrandIcon({ children, color, size = 24, viewBox = "0 0 24 24" }) {
  return (
    <Box
      component="svg"
      viewBox={viewBox}
      sx={actionIconStyles.icon({ size, color })}
      aria-hidden="true"
    >
      {children}
    </Box>
  );
}

export function LinkedinBrandIcon({
  color = PURPLE,
  size = 24,
  foregroundColor = "#FFFFFF",
  showBackground = true }) {
  return (
    <BrandIcon color={color} size={size}>
      {showBackground ? <rect x="2" y="2" width="20" height="20" rx="3.25" fill="currentColor" /> : null}
      <circle cx="7.05" cy="7.1" r="1.22" fill={foregroundColor} />
      <rect x="5.8" y="9.5" width="2.5" height="8.15" rx="0.5" fill={foregroundColor} />
      <path
        d="M10.45 9.5H12.85V10.65C13.43 9.92 14.33 9.4 15.62 9.4C17.92 9.4 18.8 10.88 18.8 13.17V17.65H16.35V13.6C16.35 12.54 15.97 11.73 14.9 11.73C13.8 11.73 13.15 12.47 13.15 13.69V17.65H10.45V9.5Z"
        fill={foregroundColor}
      />
    </BrandIcon>
  );
}

export function FacebookBrandIcon({
  color = PURPLE,
  size = 24,
  foregroundColor = "#FFFFFF",
  showBackground = true }) {
  return (
    <BrandIcon color={color} size={size}>
      {showBackground ? <circle cx="12" cy="12" r="9.25" fill="currentColor" /> : null}
      <path
        d="M13.78 7.04H15.32V4.55C15.05 4.51 14.14 4.43 13.08 4.43C10.85 4.43 9.32 5.79 9.32 8.36V10.45H6.86V13.29H9.32V19.4H12.26V13.29H14.68L15.04 10.45H12.26V8.64C12.26 7.83 12.48 7.27 13.78 7.27V7.04Z"
        fill={foregroundColor}
      />
    </BrandIcon>
  );
}

function GoogleBrandIcon({ color, size }) {
  return (
    <BrandIcon color={color} size={size}>
      <path
        d="M19.1 12.25C19.1 16.25 16.35 19 12.4 19C8.45 19 5.2 15.75 5.2 11.8C5.2 7.85 8.45 4.6 12.4 4.6C14.3 4.6 15.9 5.3 17.1 6.45L15.15 8.35C14.45 7.7 13.55 7.35 12.4 7.35C9.95 7.35 8 9.4 8 11.8C8 14.2 9.95 16.25 12.4 16.25C14.65 16.25 16.15 14.95 16.45 13.1H12.4V10.55H18.85C19 11 19.1 11.55 19.1 12.25Z"
        fill="currentColor"
      />
    </BrandIcon>
  );
}

function InstagramBrandIcon({ color, size }) {
  return (
    <BrandIcon color={color} size={size} viewBox="0 0 20 20">
      <rect
        x="2.1"
        y="2.1"
        width="15.8"
        height="15.8"
        rx="4.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle
        cx="10"
        cy="10"
        r="3.65"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="14.55" cy="5.75" r="1.15" fill="currentColor" />
    </BrandIcon>
  );
}

function YoutubeBrandIcon({ color, size }) {
  return (
    <BrandIcon color={color} size={size} viewBox="0 0 20 20">
      <rect
        x="1.1"
        y="3.7"
        width="17.8"
        height="12.6"
        rx="3.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path d="M7.55 7.2L13.85 10L7.55 12.8V7.2Z" fill="currentColor" />
    </BrandIcon>
  );
}

function TiktokBrandIcon({ color, size }) {
  return (
    <BrandIcon color={color} size={size}>
      <path d="M13.85 6.2C14.2 7.35 15.2 8.25 16.55 8.45V10.6C15.4 10.52 14.45 10.15 13.7 9.48V14.05C13.7 16.42 11.87 18.15 9.75 18.15C7.63 18.15 5.8 16.42 5.8 14.05C5.8 11.68 7.63 9.95 9.75 9.95C10.13 9.95 10.47 10.01 10.8 10.13V12.35C10.5 12.15 10.15 12.03 9.75 12.03C8.77 12.03 7.95 12.87 7.95 14.05C7.95 15.23 8.77 16.08 9.75 16.08C10.73 16.08 11.55 15.23 11.55 14.05V5.85H13.85V6.2Z" fill="currentColor" />
    </BrandIcon>
  );
}

function groupOrder(type = "") {
  if (CONTACT_TYPES.includes(type)) return 0;
  if (SOCIAL_TYPES.includes(type)) return 1;
  if (OTHER_TYPES.includes(type)) return 2;
  return 3;
}

function typeOrder(type = "") {
  return [...CONTACT_TYPES, ...SOCIAL_TYPES, ...OTHER_TYPES].indexOf(type);
}

const normalizeHref = (type, href) => {
  const normalizedValue = normalizeContactActionHref(type, href);
  if (!normalizedValue) return "";

  if (type === "email") {
    return normalizedValue.startsWith("mailto:")
      ? normalizedValue
      : `mailto:${normalizedValue}`;
  }

  if (type === "phone" || type === "sms") {
    return normalizedValue.startsWith("tel:")
      ? normalizedValue
      : `tel:${normalizedValue}`;
  }

  return normalizedValue;
};

const getLabel = (type) => {
  switch (type) {
    case "linkedin":
      return "LinkedIn";
    case "email":
      return "Email";
    case "website":
      return "Website";
    case "phone":
      return "Phone";
    case "facebook":
      return "Facebook";
    case "google":
      return "Google";
    case "instagram":
      return "Instagram";
    case "youtube":
      return "YouTube";
    case "tiktok":
      return "TikTok";
    case "twitter":
    case "x":
      return "X";
    case "sms":
      return "SMS";
    case "resume":
      return "Resume";
    default:
      return "Link";
  }
};

const renderIcon = (type, { color, iconSize, variant, disabled }) => {
  const resolvedSize = Number(iconSize) > 0 ? Number(iconSize) : 24;

  switch (type) {
    case "linkedin":
      if (variant === "filled") {
        return (
          <LinkedinBrandIcon
            color="transparent"
            size={resolvedSize}
            foregroundColor="#FFFFFF"
            showBackground={false}
          />
        );
      }
      return (
        <svg width={resolvedSize} height={resolvedSize} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={actionIconStyles.websiteIcon()}>
          <g clipPath="url(#clip0_929_61041)">
            <path d="M18.5195 0H1.47656C0.660156 0 0 0.644531 0 1.44141V18.5547C0 19.3516 0.660156 20 1.47656 20H18.5195C19.3359 20 20 19.3516 20 18.5586V1.44141C20 0.644531 19.3359 0 18.5195 0ZM5.93359 17.043H2.96484V7.49609H5.93359V17.043ZM4.44922 6.19531C3.49609 6.19531 2.72656 5.42578 2.72656 4.47656C2.72656 3.52734 3.49609 2.75781 4.44922 2.75781C5.39844 2.75781 6.16797 3.52734 6.16797 4.47656C6.16797 5.42188 5.39844 6.19531 4.44922 6.19531ZM17.043 17.043H14.0781V12.4023C14.0781 11.2969 14.0586 9.87109 12.5352 9.87109C10.9922 9.87109 10.7578 11.0781 10.7578 12.3242V17.043H7.79688V7.49609H10.6406V8.80078H10.6797C11.0742 8.05078 12.043 7.25781 13.4844 7.25781C16.4883 7.25781 17.043 9.23438 17.043 11.8047V17.043Z" fill={color} />
          </g>
          <defs>
            <clipPath id="clip0_929_61041">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case "facebook":
      return (
        <svg width={resolvedSize} height={resolvedSize} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={actionIconStyles.linkedInIcon()}>
          <g clipPath="url(#clip0_1136_59667)">
            <path d="M10 0C4.4772 0 0 4.4772 0 10C0 14.6896 3.2288 18.6248 7.5844 19.7056V13.056H5.5224V10H7.5844V8.6832C7.5844 5.2796 9.1248 3.702 12.4664 3.702C13.1 3.702 14.1932 3.8264 14.6404 3.9504V6.7204C14.4044 6.6956 13.9944 6.6832 13.4852 6.6832C11.8456 6.6832 11.212 7.3044 11.212 8.9192V10H14.4784L13.9172 13.056H11.212V19.9268C16.1636 19.3288 20.0004 15.1128 20.0004 10C20 4.4772 15.5228 0 10 0Z" fill={color} />
          </g>
          <defs>
            <clipPath id="clip0_1136_59667">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case "google":
      return <GoogleBrandIcon color={color} size={resolvedSize} />;
    case "instagram":
      return <InstagramBrandIcon color={color} size={resolvedSize} />;
    case "youtube":
      return <YoutubeBrandIcon color={color} size={resolvedSize} />;
    case "tiktok":
      return <TiktokBrandIcon color={color} size={resolvedSize} />;
    case "twitter":
    case "x":
      return (
        <svg width={resolvedSize} height={resolvedSize} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={actionIconStyles.socialIcon()}>
          <path d="M15.2719 1.58655H18.0832L11.9415 8.60612L19.1667 18.1582H13.5094L9.0784 12.3649L4.00833 18.1582H1.1954L7.76457 10.65L0.833374 1.58655H6.6343L10.6395 6.88182L15.2719 1.58655ZM14.2853 16.4755H15.843L5.78787 3.18082H4.11626L14.2853 16.4755Z" fill={color} />
        </svg>
      );
    case "email":
      return <FigmaIcon name="tableEmail" size={resolvedSize} color={color} />;
    case "website":
      return <FigmaIcon name="global" size={resolvedSize} color={color} />;
    case "phone":
      return <FigmaIcon name="call" size={resolvedSize} color={color} />;
    case "sms":
      return <FigmaIcon name="tableEmail" size={resolvedSize} color={color} />;
    case "resume":
      return <FigmaIcon name="tableDocument" size={resolvedSize} color={color} />;
    default:
      return null;
  }
};

export default function InfoActionIcons({
  actions = [],
  showDisabled = false,
  showGroupDivider = true,
  gap = 1.25,
  iconSize = 24,
  variant = "default",
  filledSize = 34,
  filledWidth,
  filledHeight,
  preserveOrder = false }) {
  const genericDataTableRules = useContext(GenericDataTableContext);
  const resolvedGap = genericDataTableRules?.iconGap ?? gap;
  const suppressedActionTypeSet = new Set(
    Array.isArray(genericDataTableRules?.suppressActionTypes)
      ? genericDataTableRules.suppressActionTypes
          .map((type) => String(type || "").trim().toLowerCase())
          .filter(Boolean)
      : []
  );
  const suppressedDisabledActionTypeSet = new Set(
    Array.isArray(genericDataTableRules?.suppressDisabledActionTypes)
      ? genericDataTableRules.suppressDisabledActionTypes
          .map((type) => String(type || "").trim().toLowerCase())
          .filter(Boolean)
      : []
  );
  const filteredActions = (actions || [])
    .map((action) => {
      if (!action) return null;
      const type = action.type || "website";
      return {
        ...action,
        __normalizedHref: normalizeHref(type, action.href) };
    })
    .filter((action) => {
      if (!action) return false;
      if (
        suppressedActionTypeSet.has(
          String(action.type || "website").trim().toLowerCase()
        )
      ) {
        return false;
      }

      const normalizedHref = String(action.__normalizedHref || "").trim();
      if (normalizedHref) return true;
      if (!showDisabled) return false;

      return !suppressedDisabledActionTypeSet.has(
        String(action.type || "website").trim().toLowerCase()
      );
    });
  // Builders place the main website first. Additional unrecognized social
  // links also use "website", but table cells should have only one globe.
  const isWebsiteAction = (action) => (action.type || "website") === "website";
  const primaryWebsiteAction = filteredActions.find((action) =>
    isWebsiteAction(action) && action.__normalizedHref && !action.disabled
  ) || filteredActions.find(isWebsiteAction);
  const tableActions = genericDataTableRules?.singleWebsite
    ? filteredActions.filter((action) => !isWebsiteAction(action) || action === primaryWebsiteAction)
    : filteredActions;
  const visibleActions = preserveOrder
    ? tableActions
    : [...tableActions].sort((left, right) => {
        const leftType = left?.type || "website";
        const rightType = right?.type || "website";
        const groupDelta = groupOrder(leftType) - groupOrder(rightType);
        if (groupDelta !== 0) return groupDelta;
        return typeOrder(leftType) - typeOrder(rightType);
      });

  if (!visibleActions.length) return null;

  if (preserveOrder) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={resolvedGap}
        useFlexGap
        sx={actionIconStyles.group({ resolvedGap })}
      >
        {visibleActions.map((action, index) => renderAction(action, index))}
      </Stack>
    );
  }

  const contactActions = visibleActions.filter((action) => CONTACT_TYPES.includes(action?.type || "website"));
  const socialActions = visibleActions.filter(
    (action) => SOCIAL_TYPES.includes(action?.type || "website")
  );
  const otherActions = visibleActions.filter(
    (action) => !CONTACT_TYPES.includes(action?.type || "website") && !SOCIAL_TYPES.includes(action?.type || "website")
  );

  function renderAction(action, index) {
    const type = action.type || "website";
    const href = action.__normalizedHref || normalizeHref(type, action.href);
    const disabled = action.disabled ?? !href;
    const isFilled = variant === "filled";
    const color = disabled ? DISABLED : PURPLE;
    const iconColor = isFilled ? "#FFFFFF" : color;
    const pillWidth = filledWidth || filledSize;
    const pillHeight = filledHeight || filledSize;
    const Component = disabled ? "span" : "a";
    const title = action.title || getLabel(type);
    const openInNewTab = !["email", "phone", "sms"].includes(type);

    return (
      <Box
        key={`${type}-${action.href || "empty"}-${index}`}
        component={Component}
        href={disabled ? undefined : href}
        target={disabled || !openInNewTab ? undefined : "_blank"}
        rel={disabled || !openInNewTab ? undefined : "noopener noreferrer"}
        onClick={(event) => {
          // These icons are often rendered inside a clickable card. Keep the
          // channel action (email, phone, LinkedIn, etc.) from also opening
          // the card's profile view.
          event.stopPropagation();
          action.onClick?.(event);
        }}
        title={title}
        aria-label={title}
        sx={actionIconStyles.action({ isFilled, pillWidth, pillHeight, iconColor, disabled })}
      >
        {renderIcon(type, {
          color: iconColor,
          iconSize: genericDataTableRules ? (SOCIAL_TYPES.includes(type) ? 20 : genericDataTableRules.iconSize ?? iconSize) : iconSize,
          variant,
          disabled })}
      </Box>
    );
  }

  const renderGroup = (group) => (
    <Stack
      direction="row"
      spacing={resolvedGap}
      alignItems="center"
      useFlexGap
      sx={actionIconStyles.singleGroup({ resolvedGap })}
    >
      {group.map((action, index) => renderAction(action, index))}
    </Stack>
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={resolvedGap}
      useFlexGap
      sx={actionIconStyles.groupedActions({ resolvedGap })}
    >
      {contactActions.length ? renderGroup(contactActions) : null}
      {showGroupDivider && contactActions.length && socialActions.length ? (
        <Box
          aria-hidden="true"
          sx={actionIconStyles.divider({ resolvedGap })}
        />
      ) : null}
      {socialActions.length ? renderGroup(socialActions) : null}
      {otherActions.length ? renderGroup(otherActions) : null}
    </Stack>
  );
}
